import { apiClient } from '../utils/api-client';
import type { CreateAttemptMeta } from '../types/climb-log';

export interface Attempt {
  id: string;
  attempt_type: 'try' | 'zone' | 'top';
  attempted_at: string;
  user: {
    id: string;
    full_name: string;
    slug: string;
  };
  climb: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface UpdateAttemptData {
  attempted_at: string;
}

export interface CreateAttemptResult {
  attempt: Attempt;
  meta?: CreateAttemptMeta;
}

export interface AttemptResponse {
  meta?: CreateAttemptMeta;
  data: {
    id: string;
    type: 'attempt';
    attributes: {
      attempt_type: 'try' | 'zone' | 'top';
      attempted_at: string;
      created_at: string;
      user: {
        id: string;
        full_name: string;
        slug: string;
      };
      climb: {
        id: string;
        name: string;
        slug: string;
      };
    };
    relationships?: {
      user: {
        data: {
          id: string;
          type: 'user';
        };
      };
      climb: {
        data: {
          id: string;
          type: 'climb';
        };
      };
    };
  };
  included?: Array<{
    id: string;
    type: 'user' | 'climb';
    attributes: Record<string, unknown>;
  }>;
}

export interface AttemptsListResponse {
  data: Array<{
    id: string;
    type: 'attempt';
    attributes: {
      attempt_type: 'try' | 'zone' | 'top';
      attempted_at: string;
      created_at: string;
      user?: {
        id: string;
        full_name: string;
        slug: string;
      };
      climb?: {
        id: string;
        name: string;
        slug: string;
      };
    };
    relationships?: {
      user: {
        data: {
          id: string;
          type: 'user';
        };
      };
      climb: {
        data: {
          id: string;
          type: 'climb';
        };
      };
    };
  }>;
  included?: Array<{
    id: string;
    type: 'user' | 'climb';
    attributes: Record<string, unknown>;
  }>;
}

interface CreateAttemptData {
  attempt_type: 'try' | 'zone' | 'top';
  attempted_at?: string;
}

class AttemptsService {
  // Get attempts for a climb
  async getClimbAttempts(climbSlug: string, userSlug?: string): Promise<Attempt[]> {
    try {
      const url = userSlug
        ? `/api/climbs/${climbSlug}/attempts?user_id=${userSlug}`
        : `/api/climbs/${climbSlug}/attempts`;

      const response = await apiClient.get(url, { requireAuth: true });
      const data: AttemptsListResponse = await response.json();
      return this.transformAttemptsResponse(data);
    } catch (error) {
      console.error('Failed to fetch climb attempts:', error);
      throw error;
    }
  }

  // Get attempts for a user
  async getUserAttempts(userSlug: string): Promise<Attempt[]> {
    try {
      const response = await apiClient.get(`/api/users/${userSlug}/attempts`, { requireAuth: true });
      const data: AttemptsListResponse = await response.json();
      return this.transformAttemptsResponse(data);
    } catch (error) {
      console.error('Failed to fetch user attempts:', error);
      throw error;
    }
  }

  // Create an attempt for a climb
  async createAttempt(climbSlug: string, data: CreateAttemptData): Promise<CreateAttemptResult> {
    try {
      const now = new Date()
      const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      const response = await apiClient.post(`/api/climbs/${climbSlug}/attempts`, {
        data: {
          type: 'attempt',
          attributes: {
            attempt_type: data.attempt_type,
            attempted_at: data.attempted_at ?? localDate,
          }
        }
      }, { requireAuth: true });

      const responseData: AttemptResponse = await response.json();
      return {
        attempt: this.transformAttemptResponse(responseData),
        meta: responseData.meta
      };
    } catch (error) {
      console.error('Failed to create attempt:', error);
      throw error;
    }
  }

  // Update an attempt (e.g. change attempted_at date)
  async updateAttempt(climbSlug: string, attemptId: string, data: UpdateAttemptData): Promise<Attempt> {
    try {
      const response = await apiClient.patch(`/api/climbs/${climbSlug}/attempts/${attemptId}`, {
        data: {
          type: 'attempt',
          attributes: data
        }
      }, { requireAuth: true });
      const responseData: AttemptResponse = await response.json();
      return this.transformAttemptResponse(responseData);
    } catch (error) {
      console.error('Failed to update attempt:', error);
      throw error;
    }
  }

  // Delete an attempt
  async deleteAttempt(climbSlug: string, attemptId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/climbs/${climbSlug}/attempts/${attemptId}`, { requireAuth: true });
    } catch (error) {
      console.error('Failed to delete attempt:', error);
      throw error;
    }
  }

  // Transform JSON:API response to Attempt objects
  private transformAttemptsResponse(response: AttemptsListResponse): Attempt[] {
    const { data, included = [] } = response;

    return data.map(attemptData => {
      // Check if data is already embedded in attributes (create/single response)
      if (attemptData.attributes.user && attemptData.attributes.climb) {
        return {
          id: attemptData.id,
          attempt_type: attemptData.attributes.attempt_type,
          attempted_at: attemptData.attributes.attempted_at || attemptData.attributes.created_at,
          user: {
            id: attemptData.attributes.user.id,
            full_name: attemptData.attributes.user.full_name,
            slug: attemptData.attributes.user.slug
          },
          climb: {
            id: attemptData.attributes.climb.id,
            name: attemptData.attributes.climb.name,
            slug: attemptData.attributes.climb.slug
          }
        };
      }

      // Fallback to relationships/included format (list responses)
      const user = attemptData.relationships ? included.find(inc =>
        inc.type === 'user' && inc.id === attemptData.relationships!.user.data.id
      ) : undefined;

      const climb = attemptData.relationships ? included.find(inc =>
        inc.type === 'climb' && inc.id === attemptData.relationships!.climb.data.id
      ) : undefined;

      return {
        id: attemptData.id,
        attempt_type: attemptData.attributes.attempt_type,
        attempted_at: attemptData.attributes.attempted_at || attemptData.attributes.created_at || new Date().toISOString(),
        user: user && user.attributes ? {
          id: user.id,
          full_name: user.attributes.full_name as string,
          slug: user.attributes.slug as string
        } : {
          id: attemptData.relationships?.user.data.id || 'unknown',
          full_name: 'Unknown User',
          slug: 'unknown'
        },
        climb: climb && climb.attributes ? {
          id: climb.id,
          name: climb.attributes.name as string,
          slug: climb.attributes.slug as string
        } : {
          id: attemptData.relationships?.climb.data.id || 'unknown',
          name: 'Unknown Climb',
          slug: 'unknown'
        }
      };
    });
  }

  private transformAttemptResponse(response: AttemptResponse): Attempt {
    const attemptData = response.data;

    return {
      id: attemptData.id,
      attempt_type: attemptData.attributes.attempt_type,
      attempted_at: attemptData.attributes.attempted_at || attemptData.attributes.created_at,
      user: {
        id: attemptData.attributes.user.id,
        full_name: attemptData.attributes.user.full_name,
        slug: attemptData.attributes.user.slug
      },
      climb: {
        id: attemptData.attributes.climb.id,
        name: attemptData.attributes.climb.name,
        slug: attemptData.attributes.climb.slug
      }
    };
  }
}

export const attemptsService = new AttemptsService();
